import { addDays, addMonths, startOfDay, startOfMonth, startOfQuarter, startOfYear, format } from 'date-fns'

/** Date-range presets for the dashboard filter row; ranges are [from, to) in the viewer's local time. */
export const RANGE_PRESETS = [
  { id: 'last7', label: 'Last 7 days', range: (now) => [addDays(startOfDay(now), -6), addDays(startOfDay(now), 1)] },
  { id: 'last30', label: 'Last 30 days', range: (now) => [addDays(startOfDay(now), -29), addDays(startOfDay(now), 1)] },
  { id: 'last90', label: 'Last 90 days', range: (now) => [addDays(startOfDay(now), -89), addDays(startOfDay(now), 1)] },
  { id: 'thisMonth', label: 'This month', range: (now) => [startOfMonth(now), addMonths(startOfMonth(now), 1)] },
  { id: 'thisQuarter', label: 'This quarter', range: (now) => [startOfQuarter(now), addMonths(startOfQuarter(now), 3)] },
  { id: 'thisYear', label: 'This year', range: (now) => [startOfYear(now), addMonths(startOfYear(now), 12)] },
  { id: 'all', label: 'All time', range: () => [null, null] },
]

/** Backend expects LocalDateTime (no zone): the viewer's wall clock. */
const toLocalIso = (d) => (d ? format(d, "yyyy-MM-dd'T'HH:mm:ss") : null)

export const resolveRange = (presetId) => {
  const preset = RANGE_PRESETS.find((p) => p.id === presetId) || RANGE_PRESETS[1]
  const [from, to] = preset.range(new Date())
  return { from: toLocalIso(from), to: toLocalIso(to), label: preset.label }
}

/** Sensible bucket size for a trend over the selected range. */
export const intervalFor = (presetId) => ({ last7: 'DAY', last30: 'DAY', thisMonth: 'DAY', last90: 'WEEK', thisQuarter: 'WEEK', thisYear: 'MONTH', all: 'MONTH' }[presetId] || 'DAY')

export const WIDGET_KINDS = [
  { kind: 'NUMBER', label: 'Number', hint: 'One headline figure, e.g. open deals' },
  { kind: 'BAR', label: 'Bar chart', hint: 'Compare groups, e.g. deals by stage' },
  { kind: 'TREND', label: 'Trend', hint: 'Change over time, e.g. leads per week' },
  { kind: 'TABLE', label: 'Top list', hint: 'Records ranked, e.g. biggest deals' },
]

export const WIDTHS = [
  { value: 4, label: 'Small (⅓)' },
  { value: 6, label: 'Medium (½)' },
  { value: 12, label: 'Full width' },
]

let counter = 0
export const widgetId = () => `w${Date.now().toString(36)}${(counter++).toString(36)}`

export const newWidget = (entityType) => ({
  id: widgetId(),
  title: '',
  kind: 'NUMBER',
  entityType,
  fn: 'COUNT',
  measureField: null,
  groupBy: null,
  dateField: 'createdOn',
  interval: null, // null = follow the dashboard range
  filters: {},
  compare: true,
  limit: 10,
  width: 4,
})

/** Widget definition + dashboard range → widget-data request. */
export const toQuery = (widget, rangeId) => {
  const { from, to } = widget.dateField === 'none' ? { from: null, to: null } : resolveRange(rangeId)
  return {
    kind: widget.kind,
    entityType: widget.entityType,
    fn: widget.fn || 'COUNT',
    measureField: widget.fn === 'COUNT' ? null : widget.measureField,
    groupBy: widget.kind === 'BAR' ? widget.groupBy : null,
    dateField: widget.dateField || 'createdOn',
    interval: widget.kind === 'TREND' ? widget.interval || intervalFor(rangeId) : null,
    filters: widget.filters || {},
    from,
    to,
    compare: widget.kind === 'NUMBER' && !!widget.compare && !!from,
    limit: widget.kind === 'BAR' || widget.kind === 'TABLE' ? widget.limit || 10 : null,
  }
}

/** A readable default title from the definition, e.g. "Total amount of deals by stage". */
export const autoTitle = (widget, fieldsByKey, entityLabelPlural) => {
  const measure = widget.fn === 'COUNT' || !widget.measureField
    ? entityLabelPlural
    : `${widget.fn === 'SUM' ? 'Total' : 'Average'} ${(fieldsByKey[widget.measureField]?.label || '').toLowerCase()}`
  if (widget.kind === 'BAR' && widget.groupBy) return `${measure} by ${(fieldsByKey[widget.groupBy]?.label || '').toLowerCase()}`
  if (widget.kind === 'TREND') return `${measure} over time`
  if (widget.kind === 'TABLE') return widget.measureField ? `Top ${entityLabelPlural.toLowerCase()} by ${(fieldsByKey[widget.measureField]?.label || '').toLowerCase()}` : `Latest ${entityLabelPlural.toLowerCase()}`
  return measure
}
